<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  routePlansApi, isEnrichedShift, type RoutePlan, type EnrichedExistingShift,
} from '@/api/route-plans';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import TimeInput from '@/components/time-input.vue';

// Editor for an EXISTING shift on the board: the shift itself (patient, duration) is read-only;
// only WHO attends it and WHEN are editable. The change is a plan proposal persisted on apply.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
  shiftId: number | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();

const caregiverId = ref<number | null>(null);
const startTime = ref('');
const busy = ref(false);
const errorText = ref('');

// The step and its current column, straight from the plan's presentation.
const located = computed<{ step: EnrichedExistingShift; caregiverId: number } | null>(() => {
  for (const [id, steps] of Object.entries(props.routePlan.caregiverExistingShifts)) {
    const step = steps.filter(isEnrichedShift).find(s => s.shiftId === props.shiftId);
    if (step) return { step, caregiverId: Number(id) };
  }

  return null;
});

const step = computed(() => located.value?.step ?? null);

const caregiverOptions = computed(() => props.routePlan.routePlanCaregivers.map((rpc) => ({
  value: rpc.caregiverId,
  title: rpc.caregiverFullName,
})));

function seed() {
  errorText.value = '';
  caregiverId.value = located.value?.caregiverId ?? null;
  startTime.value = step.value?.humanStartTime ?? '';
}

watch(() => props.modelValue, (open) => {
  if (open) seed();
}, { immediate: true });

const canSave = computed(() => !!step.value && caregiverId.value !== null && !!startTime.value);

function requestError(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: string[] } } })?.response?.data;

  return data?.errors?.length ? data.errors.join(' · ') : t('routePlan.shiftEdit.saveError');
}

async function onSave() {
  if (!canSave.value || busy.value) return;
  busy.value = true;
  errorText.value = '';

  try {
    const { warnings } = await routePlansApi.moveExistingShift(
      props.routePlan.id, step.value!.shiftId, caregiverId.value!,
      `${props.routePlan.date} ${startTime.value}`,
    );
    emit('saved', warnings);
    emit('update:modelValue', false);
  } catch (error) {
    errorText.value = requestError(error);
  } finally {
    busy.value = false;
  }
}

async function onRevert() {
  if (!step.value || busy.value) return;
  busy.value = true;

  try {
    await routePlansApi.revertShiftMove(props.routePlan.id, step.value.shiftId);
    emit('saved', []);
    emit('update:modelValue', false);
  } catch (error) {
    errorText.value = requestError(error);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="600"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card
      v-if="step"
      class="pa-2"
    >
      <v-card-title class="d-flex align-center flex-wrap ga-2">
        <span class="text-truncate">{{ step.patientFullName }}</span>
        <v-chip
          size="small"
          color="grey-darken-1"
          variant="tonal"
        >
          {{ t('routePlan.shiftEdit.existingShift') }}
        </v-chip>
        <v-chip
          v-if="step.unplanned"
          size="small"
          color="warning"
          variant="tonal"
        >
          {{ t('routePlan.shiftEdit.unplannedChip') }}
        </v-chip>
        <v-chip
          v-if="step.moved"
          size="small"
          color="warning"
          variant="tonal"
        >
          {{ t('routePlan.shiftEdit.movedTooltip', {
            time: `${step.originalStartTime}–${step.originalEndTime}` }) }}
        </v-chip>
        <v-spacer />
        <v-btn
          v-if="step.moved"
          icon
          size="small"
          variant="outlined"
          color="grey-darken-1"
          :disabled="busy"
          :aria-label="t('routePlan.shiftEdit.revert')"
          @click="onRevert"
        >
          <v-icon>mdi-restore</v-icon>
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            {{ t('routePlan.shiftEdit.revert') }}
          </v-tooltip>
        </v-btn>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="t('routePlan.cancel')"
          @click="emit('update:modelValue', false)"
        />
      </v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-6"
        >
          {{ t('routePlan.shiftEdit.subtitle') }}
        </v-alert>

        <v-sheet class="pa-4 rounded-lg bg-grey-lighten-4">
          <div class="text-subtitle-2 font-weight-medium mb-2">
            {{ t('routePlan.shiftEdit.requirementTitle') }}
          </div>
          <div
            v-if="step.patientAddress"
            class="d-flex align-center py-1 text-body-2"
          >
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-map-marker-outline
            </v-icon>
            <span>{{ step.patientAddress }}</span>
          </div>
          <div class="d-flex align-center py-1 text-body-2">
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-clock-outline
            </v-icon>
            <span>
              {{ t('routePlan.board.fixedAt',
                   { time: `${step.originalStartTime}–${step.originalEndTime}` }) }}
            </span>
          </div>
        </v-sheet>

        <v-row class="mt-3">
          <v-col
            cols="12"
            sm="7"
          >
            <v-select
              v-model="caregiverId"
              :items="caregiverOptions"
              :label="t('routePlan.visitForm.caregiver')"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col
            cols="12"
            sm="5"
          >
            <time-input
              v-model="startTime"
              density="comfortable"
              :label="t('routePlan.shiftEdit.startTime')"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canSave"
          @click="onSave"
        >
          {{ t('routePlan.visitForm.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>
